import React, { useState, useEffect } from 'react';
import './Dashboard.css';

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState({
    total_vehicles: 0,
    active_trips: 0,
    overdue: 0,
    today_trips: 0,
    boarded: 0,
    unboarded: 0,
    daily_activity: [],
    recent_trips: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
    fetchRecentTrips(); // Fetch recent trips separately
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(process.env.REACT_APP_API_BASE_URL+'/dashboard.php', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setStats(prev => ({ ...prev, ...data.data }));
      } else {
        setError('Failed to fetch dashboard data');
      }
    } catch (error) {
      setError('Error connecting to server');
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentTrips = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(process.env.REACT_APP_API_BASE_URL+'/trips.php?recent=true', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setStats(prev => ({ ...prev, recent_trips: data.data }));
      }
    } catch (error) {
      console.error('Error fetching recent trips:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(process.env.REACT_APP_API_BASE_URL+'/vehicles.php', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        // Calculate trends based on total vehicles
        const totalVehicles = data.data.length;
        const yesterdayTotal = Math.floor(totalVehicles * 0.88); // Simulate 12% growth
        return { totalVehicles, yesterdayTotal };
      }
      return { totalVehicles: 0, yesterdayTotal: 0 };
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      return { totalVehicles: 0, yesterdayTotal: 0 };
    }
  };

  const getTrendText = (current, previous, label = 'from yesterday') => {
    if (current === 0 && previous === 0) return `No change ${label}`;
    if (previous === 0) return `New ${label}`;
    
    const change = ((current - previous) / previous) * 100;
    const direction = change >= 0 ? '+' : '';
    
    if (Math.abs(change) < 1) return `No significant change ${label}`;
    return `${direction}${Math.abs(change).toFixed(1)}% ${label}`;
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffInSeconds = Math.floor((now - past) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  };

  const getVehicleStatusTrend = async () => {
    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    
    try {
      const token = localStorage.getItem('token');
      
      // Get yesterday's status counts
      const yesterdayQuery = `
        SELECT 
          SUM(CASE WHEN current_status = 'boarded' THEN 1 ELSE 0 END) as boarded_yesterday,
          SUM(CASE WHEN current_status = 'unboarded' THEN 1 ELSE 0 END) as unboarded_yesterday,
          COUNT(*) as total_yesterday
        FROM vehicles
        WHERE DATE(created_at) <= '${yesterday}'
      `;
      
      // This would require a separate API endpoint or modifying the existing one
      // For now, we'll use simulated data
      return {
        boardedTrend: getTrendText(stats.boarded, Math.floor(stats.boarded * 0.92), 'from yesterday'),
        unboardedTrend: getTrendText(stats.unboarded, Math.floor(stats.unboarded * 0.92), 'from yesterday'),
        overdueTrend: getTrendText(stats.overdue, Math.floor(stats.overdue * 1.02), 'from last week'),
        todayTripsTrend: getTrendText(stats.today_trips, Math.floor(stats.today_trips * 0.85), 'from yesterday')
      };
    } catch (error) {
      console.error('Error calculating trends:', error);
      return {
        boardedTrend: 'Data unavailable',
        unboardedTrend: 'Data unavailable',
        overdueTrend: 'Data unavailable',
        todayTripsTrend: 'Data unavailable'
      };
    }
  };

  // Calculate trends based on current stats
  const trends = {
    totalVehicles: getTrendText(stats.total_vehicles, Math.floor(stats.total_vehicles * 0.88), 'from last month'),
    activeTrips: getTrendText(stats.active_trips, Math.floor(stats.active_trips * 0.92), 'from yesterday'),
    overdue: getTrendText(stats.overdue, Math.floor(stats.overdue * 1.02), 'from last week'),
    todayTrips: getTrendText(stats.today_trips, Math.floor(stats.today_trips * 0.85), 'from yesterday')
  };

  const getStatusColor = (slaStatus) => {
    switch(slaStatus) {
      case 'overdue': return '#ef4444';
      case 'active': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (action) => {
    switch(action) {
      case 'BOARD': return '🚛';
      case 'UNBOARD': return '✅';
      default: return '📋';
    }
  };

  const getStatusLabel = (action, slaStatus) => {
    if (action === 'BOARD') {
      return slaStatus === 'overdue' ? 'Overdue boarding' : 'Vehicle boarded';
    } else if (action === 'UNBOARD') {
      return 'Trip completed';
    }
    return 'Trip recorded';
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Dashboard</h1>
          <p className="welcome-text">Welcome back, <strong>{user?.full_name || user?.username || 'User'}</strong>!</p>
          <p className="date-time">{new Date().toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}</p>
        </div>
        <div className="header-actions">
          <button className="btn-refresh" onClick={fetchDashboardData}>
            <span>🔄</span> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <span>❌</span> {error}
        </div>
      )}

      <div className="quick-stats">
        <div className="stat-card">
          <div className="stat-icon" style={{background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)'}}>
            🚛
          </div>
          <div className="stat-info">
            <h3>Total Vehicles</h3>
            <p className="stat-number">{stats.total_vehicles}</p>
            {/* <p className="stat-trend" style={{color: '#10b981'}}>{trends.totalVehicles}</p> */}
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon" style={{background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'}}>
            ✅
          </div>
          <div className="stat-info">
            <h3>Active Trips</h3>
            <p className="stat-number">{stats.active_trips}</p>
            {/* <p className="stat-trend" style={{color: '#10b981'}}>{trends.activeTrips}</p> */}
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon" style={{background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'}}>
            ⏰
          </div>
          <div className="stat-info">
            <h3>Overdue</h3>
            <p className="stat-number">{stats.overdue}</p>
          {/*   <p className="stat-trend" style={{color: trends.overdue.includes('+') ? '#ef4444' : '#10b981'}}>
              {trends.overdue}
            </p> */}
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon" style={{background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'}}>
            📊
          </div>
          <div className="stat-info">
            <h3>Today's Trips</h3>
            <p className="stat-number">{stats.today_trips}</p>
          {/*   <p className="stat-trend" style={{color: '#10b981'}}>{trends.todayTrips}</p> */}
          </div>
        </div>
      </div>

      <div className="charts-row">
        <div className="chart-card">
          <div className="chart-header">
            <h3>Vehicle Status Distribution</h3>
          </div>
          <div className="status-distribution">
            <div className="status-item">
              <div className="status-dot boarded"></div>
              <div className="status-details">
                <span className="status-name">Boarded</span>
                <span className="status-count">{stats.boarded}</span>
              </div>
              <span className="status-percentage">
                {stats.total_vehicles > 0 
                  ? `${Math.round((stats.boarded / stats.total_vehicles) * 100)}%`
                  : '0%'
                }
              </span>
            </div>
            <div className="status-item">
              <div className="status-dot unboarded"></div>
              <div className="status-details">
                <span className="status-name">Unboarded</span>
                <span className="status-count">{stats.unboarded}</span>
              </div>
              <span className="status-percentage">
                {stats.total_vehicles > 0 
                  ? `${Math.round((stats.unboarded / stats.total_vehicles) * 100)}%`
                  : '0%'
                }
              </span>
            </div>
            <div className="status-item">
              <div className="status-dot overdue"></div>
              <div className="status-details">
                <span className="status-name">Overdue</span>
                <span className="status-count">{stats.overdue}</span>
              </div>
              <span className="status-percentage">
                {stats.total_vehicles > 0 
                  ? `${Math.round((stats.overdue / stats.total_vehicles) * 100)}%`
                  : '0%'
                }
              </span>
            </div>
          </div>
          
          <div className="status-summary">
            <div className="summary-item">
              <span className="summary-label">Total Vehicles:</span>
              <span className="summary-value">{stats.total_vehicles}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Availability:</span>
              <span className="summary-value" style={{color: '#10b981'}}>
                {stats.total_vehicles > 0 
                  ? `${Math.round(((stats.total_vehicles - stats.overdue) / stats.total_vehicles) * 100)}%`
                  : '0%'
                }
              </span>
            </div>
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3>Recent Activity</h3>
            <span className="header-subtitle">Latest trips and operations</span>
          </div>
          <div className="activity-list">
            {stats.recent_trips && stats.recent_trips.length > 0 ? (
              stats.recent_trips.slice(0, 5).map((trip, index) => (
                <div key={trip.id || index} className="activity-item">
                  <span 
                    className="activity-icon"
                    style={{backgroundColor: getStatusColor(trip.sla_status)}}
                  >
                    {getStatusIcon(trip.action)}
                  </span>
                  <div className="activity-content">
                    <p className="activity-title">
                      {trip.vehicle_number 
                        ? `Vehicle ${trip.vehicle_number} ${trip.action === 'BOARD' ? 'boarded' : 'unboarded'}`
                        : getStatusLabel(trip.action, trip.sla_status)
                      }
                    </p>
                    {trip.origin && (
                      <p className="activity-details">
                        {trip.action === 'BOARD' 
                          ? `From: ${trip.origin}${trip.destination ? ` → ${trip.destination}` : ''}`
                          : `Completed at: ${trip.destination || trip.origin}`
                        }
                      </p>
                    )}
                    <span className="activity-time">
                      {getTimeAgo(trip.action_time || trip.created_at)}
                    </span>
                  </div>
                  <div className={`activity-status ${trip.sla_status || 'completed'}`}>
                    {trip.sla_status === 'overdue' ? 'Overdue' : 
                     trip.action === 'BOARD' ? 'Active' : 'Completed'}
                  </div>
                </div>
              ))
            ) : (
              <>
                {/* Fallback sample activities when no recent trips */}
                <div className="activity-item">
                  <span className="activity-icon" style={{backgroundColor: '#10b981'}}>🚛</span>
                  <div className="activity-content">
                    <p className="activity-title">No recent trips recorded</p>
                    <p className="activity-details">Start by adding your first trip</p>
                    <span className="activity-time">Just now</span>
                  </div>
                  <div className="activity-status completed">Inactive</div>
                </div>
                <div className="activity-item">
                  <span className="activity-icon" style={{backgroundColor: '#6b7280'}}>📋</span>
                  <div className="activity-content">
                    <p className="activity-title">System ready</p>
                    <p className="activity-details">Dashboard initialized successfully</p>
                    <span className="activity-time">Just now</span>
                  </div>
                  <div className="activity-status completed">Ready</div>
                </div>
              </>
            )}
          </div>
          
          <div className="activity-footer">
            <span className="activity-count">
              {stats.recent_trips?.length || 0} activities recorded
            </span>
            <button 
              className="btn-view-all"
              onClick={() => window.location.hash = '#/trips'}
            >
              View All Trips →
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Dashboard;